/**
 * v3.admin.lnVerificationRules — BACKLOG-0501 Task C admin write surface.
 *
 * Edits the (cohort × citizenship × tenure × channel) → mode matrix that
 * drives the Verify-tab LN gate (PersonModal Run-LN button + cart-commit
 * preflight). Read surface lives at `/v3/config/ln-verification-rules`
 * (active-only) and `/v3/admin/ln-verification-rules` (admin sees inactive
 * rows too, so the editor can flip IsActive).
 *
 * Server routes (PCBJxChangeApi/Functions/AdminLnVerificationRulesFunction.cs):
 *   GET   /v3/admin/ln-verification-rules
 *   PATCH /v3/admin/ln-verification-rules/{id}
 *
 * Auth: admin role required (AdminAuthService Path B preferred, Path C
 * officer-code fallback). PATCH calls also send `x-pcb-officer-code` so
 * the server can stamp LastUpdatedByOfficerCode on each edit.
 *
 * Composed onto `AdminApi.lnVerificationRules` from `client.ts` via the
 * same module-augmentation pattern as `pilotOfficers` / `products` /
 * `cartRetentionPolicies` / `lexisNexisConfig`.
 */

import type {
  RequestOptions,
  ApiResult,
  LnVerificationRule,
  LnVerificationRulePatchBody,
} from '@retail-ops/shared-types';
import type { ApiHttpClient } from '../http.js';
import { buildAdminAuthHeaders } from '../admin-auth.js';

export class AdminLnVerificationRulesApi {
  public constructor(private readonly http: ApiHttpClient) {}

  /**
   * Admin list — returns ALL rules including IsActive=false. The matrix
   * editor renders inactive rows greyed-out so an admin can flip the
   * IsActive bit without re-creating.
   */
  public list(
    opts?: RequestOptions,
  ): Promise<ApiResult<ReadonlyArray<LnVerificationRule>>> {
    return this.http.get<ApiResult<ReadonlyArray<LnVerificationRule>>>(
      '/v3/admin/ln-verification-rules',
      { ...opts },
    );
  }

  /**
   * PATCH a single row. Pass only the fields you want to change; the
   * server applies them and re-stamps LastUpdatedByOfficerCode +
   * LastUpdatedUtc. Mode is validated against {REQUIRED, OPTIONAL,
   * PROHIBIT} — invalid values return INVALID_MODE.
   */
  public patch(
    bypassKey: string,
    id: number,
    body: LnVerificationRulePatchBody,
    opts?: RequestOptions,
  ): Promise<ApiResult<LnVerificationRule>> {
    const headers = buildAdminAuthHeaders(opts?.headers, bypassKey);
    return this.http.patch<ApiResult<LnVerificationRule>>(
      `/v3/admin/ln-verification-rules/${encodeURIComponent(id)}`,
      { ...opts, headers, body },
    );
  }
}

// ─────────────────────────────────────────────────────────────────────────
// Module augmentation — extend AdminApi with `.lnVerificationRules`.
// ─────────────────────────────────────────────────────────────────────────

declare module './admin.js' {
  // eslint-disable-next-line @typescript-eslint/no-shadow
  interface AdminApi {
    /** BACKLOG-0501 — LN Verification Rules matrix admin editor. */
    readonly lnVerificationRules: AdminLnVerificationRulesApi;
  }
}
